// Centralized route → SEO metadata + Schema.org mapping.
// One single place that resolves the SEO data for every route.

import { applySeo, type SeoInput } from "./seo";
import {
  applyJsonLd,
  buildArticleSchema,
  buildBreadcrumb,
  buildCollectionPage,
  buildFaqPage,
  buildOrganization,
  buildWebPage,
  buildWebSite,
  type FaqItem,
} from "./jsonld";
import { SITE_OG_IMAGE } from "./site";
import { getArticleBySlug } from "@/mocks/articles";

type PageKind = "home" | "collection" | "faq" | "webpage";

interface PageConfig {
  seo: Omit<SeoInput, "path">;
  kind: PageKind;
}

const faqData: FaqItem[] = [
  {
    question: "What is the AllChinaBuy spreadsheet?",
    answer:
      "The AllChinaBuy spreadsheet is a curated collection of product finds, seller information, and buying tips organized by category. It helps shoppers discover quality items and compare options before purchasing.",
  },
  {
    question: "Is AllChinaBuy safe to use?",
    answer:
      "AllChinaBuy is a legitimate shopping agent platform. However, like any international shopping service, buyers should follow safety practices: verify seller ratings, check QC photos, use secure payment methods, and read reviews before purchasing.",
  },
  {
    question: "How does shipping work with AllChinaBuy?",
    answer:
      "AllChinaBuy offers multiple shipping lines based on destination, weight, and parcel size. Costs depend on actual weight or volumetric weight, destination country, and chosen shipping method. Consolidation and insurance options are available.",
  },
  {
    question: "Can I trust the spreadsheet product recommendations?",
    answer:
      "The spreadsheet is an independent resource that compiles community finds and reviews. While we verify information to the best of our ability, always perform your own QC checks and read seller reviews before purchasing.",
  },
  {
    question: "What is QC and why is it important?",
    answer:
      "QC (Quality Control) is the inspection process where warehouse staff check your items for defects, sizing issues, or incorrect products before shipping. It is crucial for catching problems early and requesting exchanges or refunds.",
  },
];

const routes: Record<string, PageConfig> = {
  "/": {
    seo: {
      title:
        "AllChinaBuy Spreadsheet — Find Better Products, Compare Options & Shop Smarter",
      description:
        "Explore AllChinaBuy spreadsheet resources, product finds, seller guides, QC tips, shipping information, reviews and practical buying advice — all in one place.",
      keywords:
        "AllChinaBuy, spreadsheet, shopping guide, QC, shipping, review, coupon, discount code",
      image: SITE_OG_IMAGE,
    },
    kind: "home",
  },
  "/products": {
    seo: {
      title: "AllChinaBuy Spreadsheet Products — Browse & Buy Verified Finds",
      description:
        "Browse the complete AllChinaBuy spreadsheet product catalog across shoes, hoodies, jackets, bags, belts and more. Compare prices and buy verified finds directly.",
      keywords: "AllChinaBuy spreadsheet, products, shoes, hoodies, bags, buy",
      image: SITE_OG_IMAGE,
    },
    kind: "collection",
  },
  "/categories": {
    seo: {
      title: "AllChinaBuy Spreadsheet Categories — Shoes, Hoodies & More",
      description:
        "Explore AllChinaBuy spreadsheet categories including shoes, hoodies, t-shirts, jackets, pants, headwear, bags and accessories with direct links to top finds.",
      keywords:
        "AllChinaBuy spreadsheet, categories, shoes, hoodies, accessories",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/guides": {
    seo: {
      title: "AllChinaBuy Spreadsheet Guides — Buying Tips & How-Tos",
      description:
        "Practical AllChinaBuy spreadsheet guides covering navigation, QC inspection, seller evaluation, buying checklists and product verification for smarter shopping.",
      keywords: "AllChinaBuy spreadsheet, guides, buying tips, QC, seller guide",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/shipping": {
    seo: {
      title: "AllChinaBuy Shipping Cost Guide 2026 — Save on Delivery",
      description:
        "Understand AllChinaBuy shipping costs, lines, times, tracking and insurance. Learn how consolidation and weight factors affect delivery prices in 2026.",
      keywords: "AllChinaBuy, shipping cost, shipping guide, tracking, insurance",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/reviews": {
    seo: {
      title: "AllChinaBuy Review 2026 — Shipping, QC, Fees & Legitimacy",
      description:
        "An independent AllChinaBuy review covering fees, quality control, warehouse services, shipping options, pros and cons, and whether the platform is legitimate.",
      keywords: "AllChinaBuy review, AllChinaBuy legit, QC, fees, shipping",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/coupons": {
    seo: {
      title: "AllChinaBuy Coupon & Discount Code Guide 2026",
      description:
        "Find the latest AllChinaBuy coupons, discount codes and deals. Learn how to save on service fees, shipping and order totals with verified money-saving tips.",
      keywords: "AllChinaBuy coupon, discount code, deals, save money",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/safety": {
    seo: {
      title: "AllChinaBuy Safety Center — Is It Legit & Safe?",
      description:
        "AllChinaBuy safety guides covering URL verification, seller checks, QC photos, payment safety, scam warnings and secure buying practices.",
      keywords: "AllChinaBuy safety, legit, scam warning, payment safety, QC",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/comparisons": {
    seo: {
      title: "AllChinaBuy vs Superbuy, CSSBuy & Sugargoo Compared",
      description:
        "Compare AllChinaBuy with Superbuy, CSSBuy and Sugargoo across fees, shipping, QC, warehouse services and user experience to choose the best agent.",
      keywords:
        "AllChinaBuy vs Superbuy, CSSBuy, Sugargoo, comparison, alternatives",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/faq": {
    seo: {
      title: "AllChinaBuy Spreadsheet FAQ — Common Questions Answered",
      description:
        "Answers to frequently asked questions about the AllChinaBuy spreadsheet, shopping safety, shipping methods, QC checks and product recommendations.",
      keywords: "AllChinaBuy spreadsheet, FAQ, shipping, QC, safety",
      image: SITE_OG_IMAGE,
    },
    kind: "faq",
  },
  "/about": {
    seo: {
      title: "About AllChinaBuy Spreadsheet — Independent Resource",
      description:
        "Learn about AllChinaBuy Spreadsheet, an independent educational resource providing guides, reviews and buying tips to help shoppers make informed decisions.",
      keywords: "AllChinaBuy spreadsheet, about, independent resource, guides",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/contact": {
    seo: {
      title: "Contact AllChinaBuy Spreadsheet",
      description:
        "Get in touch with the AllChinaBuy Spreadsheet team for questions, feedback and editorial inquiries about our independent buying guides and resources.",
      keywords: "AllChinaBuy spreadsheet, contact, feedback, questions",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/disclaimer": {
    seo: {
      title: "Disclaimer — AllChinaBuy Spreadsheet",
      description:
        "Read the AllChinaBuy Spreadsheet disclaimer covering educational content, no affiliation with AllChinaBuy, and limitations of product and seller information.",
      keywords: "AllChinaBuy spreadsheet, disclaimer, educational, no affiliation",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/privacy-policy": {
    seo: {
      title: "Privacy Policy — AllChinaBuy Spreadsheet",
      description:
        "The AllChinaBuy Spreadsheet privacy policy explaining how we collect, use and protect visitor information across our independent educational website.",
      keywords: "AllChinaBuy spreadsheet, privacy policy, data protection",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
  "/terms": {
    seo: {
      title: "Terms of Service — AllChinaBuy Spreadsheet",
      description:
        "The terms of service governing use of the AllChinaBuy Spreadsheet website, including content usage, external links and user responsibilities.",
      keywords: "AllChinaBuy spreadsheet, terms of service, usage",
      image: SITE_OG_IMAGE,
    },
    kind: "webpage",
  },
};

const articleSeoMap: Record<
  string,
  { title: string; description: string; keywords: string }
> = {
  "allchinabuy-spreadsheet-guide-2026": {
    title: "AllChinaBuy Spreadsheet Guide 2026: How to Find the Best Products",
    description:
      "A complete guide to navigating the AllChinaBuy spreadsheet, understanding categories, and finding quality products with verified sellers and QC tips.",
    keywords: "AllChinaBuy spreadsheet, guide 2026, product finds, sellers, QC",
  },
  "is-allchinabuy-legit": {
    title: "Is AllChinaBuy Legit? Complete Safety & Trust Guide",
    description:
      "An independent assessment of AllChinaBuy's legitimacy, safety practices, payment protection, and how to avoid common buying mistakes.",
    keywords: "AllChinaBuy legit, safety, payment protection, buying mistakes",
  },
  "allchinabuy-vs-superbuy": {
    title: "AllChinaBuy vs Superbuy: Which Agent Should You Choose?",
    description:
      "A detailed comparison of AllChinaBuy and Superbuy covering fees, shipping, QC, warehouse services, and overall user experience.",
    keywords: "AllChinaBuy vs Superbuy, comparison, fees, shipping, QC",
  },
};

function resolvePage(pathname: string): void {
  const config = routes[pathname];

  if (!config) {
    // Fallback: unknown route gets a generic, reasonable web page treatment.
    applySeo({
      title: "AllChinaBuy Spreadsheet — Guides, Reviews & Buying Tips",
      description:
        "Independent AllChinaBuy spreadsheet guides, product finds, seller tips, shipping information and safety advice for smarter shopping.",
      keywords: "AllChinaBuy, spreadsheet, guides, reviews, shipping",
      path: "/",
      image: SITE_OG_IMAGE,
    });
    applyJsonLd([buildWebSite()]);
    return;
  }

  const { seo, kind } = config;
  const input: SeoInput = { ...seo, path: pathname };

  let schemas: object[] = [];
  if (kind === "home") {
    schemas = [buildWebSite(), buildOrganization(), buildFaqPage(faqData)];
  } else if (kind === "collection") {
    schemas = [
      buildCollectionPage({
        title: seo.title,
        description: seo.description,
        path: pathname,
      }),
    ];
  } else if (kind === "faq") {
    schemas = [
      buildWebPage({
        title: seo.title,
        description: seo.description,
        path: pathname,
      }),
      buildFaqPage(faqData),
    ];
  } else {
    schemas = [
      buildWebPage({
        title: seo.title,
        description: seo.description,
        path: pathname,
      }),
    ];
  }

  applySeo(input);
  applyJsonLd(schemas);
}

function resolveArticle(pathname: string): void {
  const slug = pathname.replace(/^\/article\//, "").replace(/\/+$/, "");
  const meta = articleSeoMap[slug];
  const article = getArticleBySlug(slug);

  if (!meta || !article) {
    resolvePage("/guides");
    return;
  }

  applySeo({
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
    path: pathname,
    ogType: "article",
    image: SITE_OG_IMAGE,
  });

  applyJsonLd([
    buildArticleSchema({
      title: meta.title,
      description: meta.description,
      path: pathname,
      datePublished: article.date,
    }),
    buildBreadcrumb([
      { name: "Home", path: "/" },
      { name: "Guides", path: "/guides" },
      { name: meta.title, path: pathname },
    ]),
  ]);
}

export function resolveSeo(pathname: string): void {
  if (pathname.startsWith("/article/")) {
    resolveArticle(pathname);
    return;
  }
  resolvePage(pathname);
}